import { TrendingSBC } from "@/api/publicRequests/trending/getTrendingSbcs";
import SBCSet from "@/components/organisms/SBCs/SBCSet/SBCSet";
import Card from "@/design-system/Card/Card";
import { cn } from "@/lib/utils";
import { voteSbc } from "@/redux/frontPage/frontPageSlice";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector } from "@/redux/user/userSlice";
import { sbcSetPath } from "@repo/shared/utils";
import { useDispatch, useSelector } from "react-redux";

type Props = {
  sbc: TrendingSBC;
  className?: string;
  /** Fires on navigation: the feed's own view_content. */
  onClick?: () => void;
};

/**
 * The SBCs-page set card, cut down for the front page. Everything that needs a
 * set of your own — progress, completion, the actions menu — is dropped by
 * `condensed`; the vote is kept, since a visitor can cast one.
 */
export const FrontPageSbcSet = ({ sbc, className, onClick }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(getUserSelector);

  const onVote = (vote: "UPVOTE" | "DOWNVOTE") => {
    if (!user.uuid) {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "upvote/downvote-sbc",
        }),
      );
      return;
    }
    dispatch(voteSbc({ setId: +sbc.id, vote }));
  };

  return (
    <div className={cn("h-full", className)}>
      <SBCSet
        {...sbc}
        condensed
        onClick={onClick}
        to={sbcSetPath(sbc)}
        solvedCount={
          sbc.repeatabilityMode !== "UNLIMITED" ? sbc.solvedCount : 0
        }
        onUpvote={() => onVote("UPVOTE")}
        onDownvote={() => onVote("DOWNVOTE")}
      />
    </div>
  );
};

export const FrontPageSbcSetSkeleton = ({
  className,
}: {
  className?: string;
}) => (
  <div className={cn("h-full", className)}>
    <Card className="flex h-full animate-pulse flex-col gap-y-4 p-5 desktop:p-2">
      <div className="flex items-start gap-x-3.5 px-0 pt-0 desktop:px-3 desktop:pt-3">
        <div className="size-12 shrink-0 rounded bg-control" />
        <div className="flex flex-1 flex-col gap-y-2">
          <div className="h-4 w-2/3 rounded bg-control" />
          <div className="h-3 w-1/3 rounded bg-control" />
        </div>
        <div className="h-6 w-20 rounded bg-control" />
      </div>
      <div className="flex flex-col gap-y-2 px-0 desktop:px-3">
        <div className="h-3 w-24 rounded bg-control" />
        <div className="h-1.5 w-full rounded-full bg-control" />
      </div>
      <div className="mt-auto flex items-center gap-x-4 px-0 pb-0 desktop:px-3 desktop:pb-3">
        <div className="h-28 w-20 shrink-0 rounded-lg bg-control" />
        <div className="flex flex-1 flex-col gap-y-2">
          <div className="h-3 w-16 rounded bg-control" />
          <div className="h-3 w-full rounded bg-control" />
          <div className="h-3 w-4/5 rounded bg-control" />
        </div>
        <div className="h-20 w-11 shrink-0 rounded bg-control" />
      </div>
    </Card>
  </div>
);
